import { Card, CardContent } from '@/components/ui/card';
import { FileText, Clock, CheckCircle, XCircle, Pause, AlertTriangle } from 'lucide-react';

interface Stats {
  total_facturas: number;
  pendientes: number;
  aprobadas: number;
  rechazadas: number;
  en_espera: number;
  criticas: number;
}

// Tipo de filtro que puede aplicarse
type FilterType = 'all' | 'pendiente' | 'aprobada' | 'rechazada' | 'en_espera' | 'criticas';

interface StatsCardsProps {
  stats?: Stats;
  isLoading: boolean;
  activeFilter?: FilterType;
  onFilterChange?: (filter: FilterType) => void;
}

const statsConfig: {
  key: keyof Stats;
  label: string;
  icon: typeof FileText;
  color: string;
  bgColor: string;
  filterValue: FilterType;
  activeRing: string;
}[] = [
  {
    key: 'total_facturas',
    label: 'Total Facturas',
    icon: FileText,
    color: 'text-gray-600',
    bgColor: 'bg-gray-100',
    filterValue: 'all',
    activeRing: 'ring-gray-400',
  },
  {
    key: 'pendientes',
    label: 'Pendientes',
    icon: Clock,
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-100',
    filterValue: 'pendiente',
    activeRing: 'ring-yellow-400',
  },
  {
    key: 'aprobadas',
    label: 'Aprobadas',
    icon: CheckCircle,
    color: 'text-green-600',
    bgColor: 'bg-green-100',
    filterValue: 'aprobada',
    activeRing: 'ring-green-400',
  },
  {
    key: 'rechazadas',
    label: 'Rechazadas',
    icon: XCircle,
    color: 'text-red-600',
    bgColor: 'bg-red-100',
    filterValue: 'rechazada',
    activeRing: 'ring-red-400',
  },
  {
    key: 'en_espera',
    label: 'En Espera',
    icon: Pause,
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
    filterValue: 'en_espera',
    activeRing: 'ring-blue-400',
  },
  {
    key: 'criticas',
    label: 'Criticas',
    icon: AlertTriangle,
    color: 'text-orange-600',
    bgColor: 'bg-orange-100',
    filterValue: 'criticas',
    activeRing: 'ring-orange-400',
  },
];

export function StatsCards({ stats, isLoading, activeFilter, onFilterChange }: StatsCardsProps) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
      {statsConfig.map((config) => {
        const Icon = config.icon;
        const value = stats ? stats[config.key] : 0;
        const isActive = activeFilter === config.filterValue;

        return (
          <Card
            key={config.key}
            className={`relative overflow-hidden cursor-pointer transition-all hover:shadow-md ${
              isActive ? `ring-2 ${config.activeRing} shadow-md` : ''
            }`}
            onClick={() => onFilterChange?.(config.filterValue)}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">{config.label}</p>
                  {isLoading ? (
                    <div className="h-8 w-12 bg-gray-200 animate-pulse rounded mt-1" />
                  ) : (
                    <p className="text-2xl font-bold">{value}</p>
                  )}
                </div>
                <div className={`p-2 rounded-full ${config.bgColor}`}>
                  <Icon className={`h-5 w-5 ${config.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

export type { FilterType };
